<template>
  <div class="p-4" data-private>
    <div class="flex items-start justify-between mb-4">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">Supplemental Data Flat File</h2>
        <p class="text-sm text-gray-500">
          Care gaps closed by the quality team, formatted to the
          <span class="font-medium">{{ payer }}</span> supplemental-data file layout.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <select
          v-model="payer"
          class="text-sm border border-gray-300 rounded px-2 py-1.5"
          @change="onPayerChange"
        >
          <option v-for="p in payers" :key="p" :value="p">{{ p }}</option>
        </select>
        <select
          v-model="layout"
          class="text-sm border border-gray-300 rounded px-2 py-1.5"
          @change="loadRows"
        >
          <option v-for="o in layoutOptions" :key="o.value" :value="o.value">
            {{ o.label }}
          </option>
        </select>
        <button
          class="text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded px-3 py-1.5 disabled:opacity-50"
          :disabled="loading || rows.length === 0"
          @click="exportFile(spec.submissionFormat || 'txt')"
        >
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
          Export
        </button>
        <button
          v-if="!spec.submissionFormat"
          class="text-sm border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded px-3 py-1.5 disabled:opacity-50"
          :disabled="loading || rows.length === 0"
          title="Review copy for validating against source documents. Not the submission format."
          @click="exportFile('xlsx')"
        >
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
          Excel
        </button>
      </div>
    </div>

    <div v-if="loading" class="py-16 text-center text-gray-400">Loading…</div>

    <div v-else-if="rows.length === 0" class="py-16 text-center">
      <p class="text-gray-500 font-medium">No submittable rows for {{ payer }}.</p>
      <div v-if="skipped" class="text-xs text-gray-500 mt-3 inline-block text-left">
        <p v-if="skipped.not_closed">
          · <span class="font-medium">{{ skipped.not_closed }}</span>
          chart review(s) did not close the gap (e.g. patient not found in EHR)
        </p>
        <p v-if="skipped.no_code" class="text-amber-600">
          · <span class="font-medium">{{ skipped.no_code }}</span>
          closure(s) have no code in the Vytalize quality-codes file
        </p>
        <p v-if="skipped.no_dos">
          · <span class="font-medium">{{ skipped.no_dos }}</span> missing date of service
        </p>
        <p v-if="skipped.no_identity">
          · <span class="font-medium">{{ skipped.no_identity }}</span>
          missing member ID, name or DOB
        </p>
        <p v-if="skipped.no_address">
          · <span class="font-medium">{{ skipped.no_address }}</span> missing member address
        </p>
        <p v-if="skipped.no_provider">
          · <span class="font-medium">{{ skipped.no_provider }}</span>
          missing servicing provider NPI, TIN or taxonomy
        </p>
        <p v-if="skipped.wrong_layout" class="text-gray-400">
          · {{ skipped.wrong_layout }} belong to the other layout for this payer
        </p>
      </div>
    </div>

    <div v-else>
      <div class="text-xs text-gray-500 mb-2">
        {{ rows.length }} rows · {{ columns.length }} columns ({{ payer }} {{ layout }} layout)
        <span v-if="skippedNote" class="text-amber-600 ml-2">· excluded: {{ skippedNote }}</span>
      </div>
      <div class="overflow-auto border border-gray-200 rounded" style="max-height: calc(100vh - 260px)">
        <table class="min-w-full text-xs">
          <thead class="sticky top-0 bg-slate-800 text-white">
            <tr>
              <th
                v-for="col in columns"
                :key="col"
                class="text-left font-medium px-2 py-2 whitespace-nowrap"
              >
                {{ col }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in rows"
              :key="i"
              :class="i % 2 ? 'bg-gray-50' : 'bg-white'"
            >
              <td
                v-for="col in columns"
                :key="col"
                class="px-2 py-1 whitespace-nowrap text-gray-700"
              >
                {{ row[col] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="!loading && excluded.length" class="mt-6">
      <h3 class="text-sm font-semibold text-gray-900">Excluded closures</h3>
      <p class="text-xs text-gray-500 mb-2">
        {{ excluded.length }} chart review closure{{ excluded.length === 1 ? '' : 's' }} for {{ payer }} that cannot go on the file, and why.
      </p>
      <div class="overflow-auto border border-gray-200 rounded" style="max-height: 420px">
        <table class="min-w-full text-xs">
          <thead class="sticky top-0 bg-slate-800 text-white">
            <tr>
              <th v-for="col in EXCLUDED_COLUMNS" :key="col.field" class="text-left font-medium px-2 py-2 whitespace-nowrap">{{ col.header }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in excluded" :key="row.work_id" :class="i % 2 ? 'bg-gray-50' : 'bg-white'">
              <td class="px-2 py-1 whitespace-nowrap text-gray-700 sensitive" data-spoof-kind="person">{{ [row.patient_last_name, row.patient_first_name].filter(Boolean).join(', ') || '—' }}</td>
              <td class="px-2 py-1 whitespace-nowrap text-gray-700 tabular-nums sensitive" data-spoof-kind="dob">{{ row.patient_dob || '—' }}</td>
              <td class="px-2 py-1 whitespace-nowrap text-gray-700 sensitive" data-spoof-kind="identifier">{{ row.patient_identifier }}</td>
              <td class="px-2 py-1 whitespace-nowrap text-gray-700 sensitive" data-spoof-kind="contract">{{ row.contract_name }}</td>
              <td class="px-2 py-1 whitespace-nowrap text-gray-700">{{ row.measure_code }}</td>
              <td class="px-2 py-1 whitespace-nowrap text-gray-700 tabular-nums">{{ row.reviewed_on }}</td>
              <td class="px-2 py-1 whitespace-nowrap text-gray-700 sensitive" data-spoof-kind="person">{{ row.reviewer || '—' }}</td>
              <td class="px-2 py-1 text-amber-700">{{ row.reason }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { beginSensitiveEgress } from '@/utils/demoEgress';
// Each payer has its own published file layout. Endpoints return the spec
// column list (plain strings, in spec order) plus rows keyed by those names.
const PAYER_SPECS = {
  Anthem: {
    endpoint: '/api/quality/supplemental-flat-file/',
    exportEndpoint: '/api/quality/supplemental-flat-file-export/',
    layouts: [
      { value: 'EMR', label: 'EMR / Encounter file' },
      { value: 'LAB', label: 'Lab file' },
    ],
    filename: (layout, stamp) => `Vytalize_${layout === 'LAB' ? 'LAB' : 'EMR'}_${stamp}_NS.txt`,
  },
  Aetna: {
    endpoint: '/api/quality/aetna-flat-file/',
    exportEndpoint: '/api/quality/aetna-flat-file-export/',
    layouts: [
      { value: 'MEDICAL', label: 'Medical file' },
      { value: 'LAB', label: 'Lab file' },
    ],
  },
  Fidelis: {
    endpoint: '/api/quality/fidelis-flat-file/',
    exportEndpoint: '/api/quality/fidelis-flat-file-export/',
    layouts: [
      { value: 'SERVICE', label: 'Service file' },
      { value: 'LAB', label: 'Lab file' },
    ],
    // Wellcare rejects the file outright on a wrong name. Fidelis assigned
    // Vytalize VYH_NY_SERVICE_* on the 2026-08-18 test review — no [CNC|ESD]
    // segment, despite the SuDS guide publishing one.
    filename: (layout, stamp) => `VYH_NY_${layout === 'LAB' ? 'LAB' : 'SERVICE'}_${stamp}.txt`,
  },
  Emblem: {
    endpoint: '/api/quality/emblem-flat-file/',
    exportEndpoint: '/api/quality/emblem-flat-file-export/',
    layouts: [
      { value: 'STANDARD', label: 'Standard supplemental file' },
    ],
    submissionFormat: 'xlsx',
    filename: (layout, stamp) => `Vytalize_EmblemHealth_Supplemental_${stamp}.xlsx`,
  },
};

const EXCLUDED_COLUMNS = [
  { field: 'patient', header: 'Patient' },
  { field: 'patient_dob', header: 'DOB' },
  { field: 'patient_identifier', header: 'Member ID' },
  { field: 'contract_name', header: 'Contract' },
  { field: 'measure_code', header: 'Measure' },
  { field: 'reviewed_on', header: 'Reviewed' },
  { field: 'reviewer', header: 'Reviewer' },
  { field: 'reason', header: 'Why it is not on the file' },
];

export default {
  name: 'Quality_FlatFile',
  data() {
    return {
      EXCLUDED_COLUMNS,
      excluded: [],
      loading: false,
      columns: [],
      rows: [],
      skipped: null,
      payer: 'Aetna',
      payers: Object.keys(PAYER_SPECS),
      layout: 'MEDICAL',
    };
  },
  computed: {
    spec() {
      return PAYER_SPECS[this.payer];
    },
    layoutOptions() {
      return this.spec.layouts;
    },
    skippedTotal() {
      if (!this.skipped) return 0;
      return Object.values(this.skipped).reduce((a, b) => a + (b || 0), 0);
    },
    skippedNote() {
      const s = this.skipped;
      if (!s) return '';
      const parts = [];
      if (s.no_code) parts.push(`${s.no_code} no sourced code`);
      if (s.no_dos) parts.push(`${s.no_dos} no date of service`);
      if (s.no_identity) parts.push(`${s.no_identity} incomplete member identity`);
      if (s.no_address) parts.push(`${s.no_address} no member address`);
      if (s.no_provider) parts.push(`${s.no_provider} incomplete servicing provider`);
      return parts.join(', ');
    },
  },
  mounted() {
    this.loadRows();
  },
  methods: {
    onPayerChange() {
      // Reset to the first layout this payer publishes.
      this.layout = this.spec.layouts[0].value;
      this.loadRows();
    },
    async loadRows() {
      this.loading = true;
      try {
        const res = await this.$axios.post(this.spec.endpoint, { layout: this.layout });
        this.columns = res.data.columns || [];
        this.rows = res.data.rows || [];
        this.skipped = res.data.skipped || null;
        this.excluded = res.data.excluded || [];
      } catch (e) {
        this.excluded = [];
        this.columns = [];
        this.rows = [];
        this.skipped = null;
      } finally {
        this.loading = false;
      }
    },
    async exportFile(format = 'txt') {
      const ticket = beginSensitiveEgress('export');
      if (!ticket) return;
      try {
        const res = await this.$axios.post(
          this.spec.exportEndpoint,
          { layout: this.layout, format },
          { responseType: 'blob' },
        );
        // The server sets the payer-compliant name; use it when the browser
        // exposes the header, else fall back to the payer's own format.
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const cd = res.headers && (res.headers['content-disposition']
          || (res.headers.get && res.headers.get('content-disposition')));
        const m = cd && /filename="?([^"]+)"?/.exec(cd);
        const fallback = format === 'xlsx' && !this.spec.submissionFormat
          ? `${this.payer}_${this.layout}_${stamp}_REVIEW.xlsx`
          : (this.spec.filename
            ? this.spec.filename(this.layout, stamp)
            : `${this.payer}_${this.layout}_${stamp}.txt`);
        const fname = m ? m[1] : fallback;
        if (!ticket.confirm()) return;
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        // surfaced via the empty-state; nothing to do here
      }
    },
  },
};
</script>
